/**
 * AddHistoricalPermitModal — add / edit an Existing Approval (Historical Permit)
 * on a parcel (CT-020). Opens on `open-historical-permit-modal`
 * (detail.parcelId, optional detail.permit for edit); mounted once in OutlineView.
 * On success dispatches `historical-permits-changed` (detail.parcelId) so the
 * parcel research views reload the child collection.
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { addHistoricalPermit, updateHistoricalPermit } from '../services/parcels.js'

export default function AddHistoricalPermitModal() {
  const [open, setOpen] = useState(false)
  const [parcelId, setParcelId] = useState('')
  const [permitId, setPermitId] = useState('')
  const [date, setDate] = useState('')
  const [name, setName] = useState('')
  const [ref, setRef] = useState('')
  const [designReview, setDesignReview] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    function onOpen(e) {
      const d = (e && e.detail) || {}
      setParcelId(d.parcelId || '')
      const p = d.permit
      setPermitId(p ? p.id : '')
      setDate(p ? (p.fields['Permit Date'] || '') : '')
      setName(p ? (p.fields['Permit Name'] || '') : '')
      setRef(p ? (p.fields['Reference Number'] || '') : '')
      setDesignReview(p ? !!p.fields['Design Review'] : false)
      setError(''); setSaving(false); setOpen(true)
    }
    window.addEventListener('open-historical-permit-modal', onOpen)
    return () => window.removeEventListener('open-historical-permit-modal', onOpen)
  }, [])

  function close() { if (!saving) setOpen(false) }

  async function submit() {
    if (!parcelId && !permitId) { setError('No parcel in context.'); return }
    const fields = {
      'Permit Date': date || null,
      'Permit Name': name.trim(),
      'Reference Number': ref.trim(),
      'Design Review': designReview,
    }
    setSaving(true); setError('')
    try {
      const res = permitId ? await updateHistoricalPermit(permitId, fields) : await addHistoricalPermit(parcelId, fields)
      if (res && res.error) throw new Error(typeof res.error === 'string' ? res.error : (res.error.message || 'Save failed'))
      setOpen(false)
      window.dispatchEvent(new CustomEvent('historical-permits-changed', { detail: { parcelId } }))
    } catch (err) {
      setError((err && err.message) || 'Could not save the approval.')
      setSaving(false)
    }
  }

  if (!open) return null

  const label = { fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 500, color: 'var(--aj-text-mid)', marginBottom: 5, display: 'block' }
  const input = { width: '100%', boxSizing: 'border-box', fontFamily: '"DM Sans",sans-serif', fontSize: 13, padding: '8px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }
  const mono = { ...input, fontFamily: '"DM Mono",monospace' }
  const btnBase = { border: 'none', borderRadius: 4, padding: '9px 20px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, cursor: 'pointer' }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-modal-id="AddHistoricalPermitModal" style={{ background: 'var(--aj-bg-elevated)', borderRadius: 12, border: '0.5px solid var(--aj-border)', width: 420, maxWidth: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '0.5px solid var(--aj-border)' }}>
          <div style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 13, color: 'var(--aj-text)' }}>{permitId ? 'Edit Approval' : 'Add Approval'}</div>
          <button onClick={close} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', padding: 4 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
        </div>
        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div><label style={label}>Permit Date</label><input style={mono} type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
          <div><label style={label}>Permit Name</label><input style={input} value={name} placeholder="e.g. New Single Family" onChange={e => setName(e.target.value)} /></div>
          <div><label style={label}>Reference Number</label><input style={mono} value={ref} placeholder="e.g. 15087" onChange={e => setRef(e.target.value)} /></div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', cursor: 'pointer' }}>
            <input type="checkbox" checked={designReview} onChange={e => setDesignReview(e.target.checked)} /> Design Review
          </label>
          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#C0392B' }}>{error}</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 20px', borderTop: '0.5px solid var(--aj-border)' }}>
          <button onClick={close} disabled={saving} style={{ ...btnBase, background: 'transparent', color: 'var(--aj-text-mid)', border: '0.5px solid var(--aj-border-mid)' }}>Cancel</button>
          <button onClick={submit} disabled={saving} style={{ ...btnBase, background: '#07272D', color: '#fff', opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : (permitId ? 'Update' : 'Add')}</button>
        </div>
      </div>
    </div>,
    document.body
  )
}
